import { useEffect, useState } from "react";
import { Mail, MessageCircle, X, Send, Loader2, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

// Reusable per-nudge channel picker + message preview. Used before sending verification /
// document / commission nudges so an admin can choose Email / WhatsApp / both and review the
// exact copy first. Calls onSend(channelsArray) which performs the real API call.
export function NudgeChannelModal({ open, action, sellerId = null, sellerName = "", count = 1, onSend, onClose, testid = "nudge-modal" }) {
  const [email, setEmail] = useState(true);
  const [whatsapp, setWhatsapp] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open || !action) return;
    setEmail(true); setWhatsapp(false); setPreview(null); setLoading(true);
    api.post("/admin/agents/nudge-preview", { action, seller_id: sellerId })
      .then((r) => setPreview(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [open, action, sellerId]);

  if (!open) return null;
  const channels = [...(email ? ["email"] : []), ...(whatsapp ? ["whatsapp"] : [])];
  const bulk = count > 1;
  const waNoPhone = whatsapp && !bulk && preview && preview.has_phone === false;

  const send = async () => {
    if (channels.length === 0) { toast.error("Pick at least one channel."); return; }
    setSending(true);
    try { await onSend(channels); onClose(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid={testid}>
      <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <h3 className="text-base font-bold text-slate-800">{preview?.title || "Send nudge"}</h3>
          <button onClick={onClose} data-testid={`${testid}-close`} className="rounded-full p-1 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-4 p-5">
          <p className="text-xs text-slate-500">
            {bulk ? <>Sending to <b>{count}</b> selected agent(s).</> : <>Sending to <b>{sellerName || preview?.name || "this agent"}</b>.</>} Choose how it should be delivered:
          </p>
          <div className="flex flex-wrap gap-2" data-testid={`${testid}-channels`}>
            <button type="button" onClick={() => setEmail((v) => !v)} data-testid={`${testid}-ch-email`}
              className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold ${email ? "border-sky-500 bg-sky-50 text-sky-700" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
              <Mail className="h-4 w-4" /> Email
            </button>
            <button type="button" onClick={() => setWhatsapp((v) => !v)} data-testid={`${testid}-ch-whatsapp`}
              className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold ${whatsapp ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </button>
          </div>
          {waNoPhone && (
            <p className="flex items-center gap-1.5 rounded-lg bg-amber-50 px-3 py-2 text-[11px] font-medium text-amber-700" data-testid={`${testid}-wa-warning`}>
              <AlertTriangle className="h-3.5 w-3.5" /> This agent has no mobile number on file — WhatsApp will be skipped.
            </p>
          )}

          {/* Preview */}
          {loading ? (
            <div className="flex h-24 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-300" /></div>
          ) : preview ? (
            <div className="space-y-3" data-testid={`${testid}-preview`}>
              {email && (
                <div className="rounded-xl border border-slate-200 p-3">
                  <p className="mb-1 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-sky-600"><Mail className="h-3.5 w-3.5" /> Email preview</p>
                  <p className="text-sm font-semibold text-slate-800">{preview.subject}</p>
                  <p className="mt-1 text-xs text-slate-500">{preview.email_message}</p>
                </div>
              )}
              {whatsapp && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-3">
                  <p className="mb-1 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-emerald-600"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp preview</p>
                  <p className="whitespace-pre-line text-xs text-slate-600">{preview.whatsapp_message}</p>
                </div>
              )}
            </div>
          ) : null}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-3">
          <button onClick={onClose} data-testid={`${testid}-cancel`} className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
          <button onClick={send} disabled={sending || channels.length === 0} data-testid={`${testid}-send`} className="inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send {channels.length === 2 ? "both" : channels[0] === "whatsapp" ? "WhatsApp" : "email"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default NudgeChannelModal;
